import React, { forwardRef } from 'react';
import { useForm } from 'react-hook-form';

import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    width: '320px',
  },
  '& .MuiOutlinedInput-input': {
    display: 'flex',
  },
}));

const TagForm = forwardRef((props, ref) => {
  const {
    onSubmit,
    tag,
    fetchTagsExtended,
    extendedTagsFetched,
    tagsFetching,
  } = props;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { tagLotNumber: tag.tagLotNumber ?? '' },
  });

  const renderMenuItems = () => {
    if (!tag.numbers) {
      return <MenuItem value={tag.tagLotNumber}>{tag.tagLotNumber}</MenuItem>;
    }

    const items = tag.numbers.map((number, index) => (
      <MenuItem
        key={index}
        value={number.taglotnumber}
        sx={{ display: 'flex' }}
      >
        <span>{number.taglotnumber}</span>
        {number.issterile && (
          <Tooltip title="Sterile" placement="top" aria-label="Sterile">
            <LocalHospitalIcon sx={{ color: '#008996', ml: '5px' }} />
          </Tooltip>
        )}
      </MenuItem>
    ));

    // Usually we fetch only the 3 latest tag lot numbers for the menu.
    // If it is an old lot number, it won't be in the list and select would be empty
    // which is incorrect behavior, so we add it to the list
    if (
      tag.tagLotNumber &&
      !tag.numbers.find((n) => n.taglotnumber === tag.tagLotNumber)
    ) {
      items.push(
        <MenuItem key={tag.numbers.length} value={tag.tagLotNumber}>
          <Typography>{tag.tagLotNumber}</Typography>
        </MenuItem>,
      );
    }

    if (!extendedTagsFetched) {
      items.push(
        <MenuItem
          onClick={fetchTagsExtended}
          disableRipple
          key={tag.numbers.length + 1}
        >
          <MoreHorizIcon sx={{ mr: 1 }} />
          More
          {tagsFetching ? (
            <CircularProgress
              color="primary"
              size={'1rem'}
              sx={{ ml: 'auto' }}
            />
          ) : null}
        </MenuItem>,
      );
    }

    return items;
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid container sx={{ pt: 1 }}>
        <StyledTextField
          select
          variant="outlined"
          fullWidth
          label="Tag Lot Number"
          defaultValue={tag.tagLotNumber || ''}
          {...register('tagLotNumber')}
          error={!!(errors && errors.tagLotNumber)}
          helperText={errors?.tagLotNumber?.message}
        >
          {renderMenuItems()}
        </StyledTextField>
      </Grid>
    </form>
  );
});

TagForm.displayName = 'TagForm';

export default TagForm;
